import { z } from 'zod';
import { strapiEntitySchema } from './common.js';

// Wire schema for the Strapi `site-content` single type, the site's only
// source of editorial copy.
//
// Two kinds of leaf, and the difference is whether an empty value is ever a
// legitimate edit:
//
//   copy(...)  editorial prose. Required. Nobody clears a page's subtitle on
//              purpose, so a missing one means a broken entry — fail loudly
//              and let the app show its retry screen.
//   fixed(...) structural labels: the site's name, nav items, page titles,
//              error strings. These are effectively constants that happen to
//              live in the CMS, so a cleared one falls back rather than
//              taking the site down over the word "Projects".
const copy = z.string();

/** A label with a built-in fallback, used where blank is never intentional. */
const fixed = (fallback: string) =>
  z
    .string()
    .nullish()
    .transform((value) =>
      value === null || value === undefined || value === '' ? fallback : value,
    );

const emptyStateSchema = z.object({ title: copy, message: copy });

// Repeatable component items are all-or-nothing: an item present on the wire
// must be complete, but the whole array may be absent.
const titledBlockSchema = z.object({ title: z.string(), body: z.string() });

const statSchema = z.object({ label: copy, caption: copy });

const valueStatSchema = z.object({ value: copy, label: copy, caption: copy });

/** Titles are structural; the subtitle and empty state are editorial. */
const pageBase = (title: string) => ({
  title: fixed(title),
  subtitle: copy,
  metaTitle: fixed(`${title} - Baharudin Yusup`),
  empty: emptyStateSchema,
});

export const strapiSiteContentSchema = strapiEntitySchema.extend({
  site: z.object({
    name: fixed('Baharudin Yusup'),
    url: fixed('https://www.baharudin.dev'),
    metaTitle: fixed(
      'Baharudin Yusup - Senior Mobile Engineer & Technical Lead',
    ),
    // The one editorial leaf here: it is a written pitch, not a label.
    metaDescription: copy,
  }),
  nav: z.object({
    about: fixed('About'),
    experience: fixed('Experience'),
    projects: fixed('Projects'),
    education: fixed('Education'),
    certifications: fixed('Certifications'),
    awards: fixed('Awards'),
  }),
  chrome: z.object({
    brand: fixed('Portfolio'),
    madeBy: fixed('Made by'),
    onThisPage: fixed('On This Page'),
    quickContact: fixed('Quick Contact'),
    getInTouch: fixed('Get in Touch'),
  }),
  pages: z.object({
    about: z.object({
      ...pageBase('Baharudin Yusup'),
      viewExperienceCta: copy,
      exploreProjectsCta: copy,
      expertiseHeading: copy,
      contactHeading: copy,
    }),
    experience: z.object(pageBase('Work Experience')),
    projects: z.object({
      ...pageBase('Projects'),
      githubCta: copy,
      collaboration: z.object({
        heading: copy,
        body: copy,
        contactCta: copy,
        githubCta: copy,
      }),
    }),
    education: z.object({
      ...pageBase('Education'),
      focusAreasHeading: copy,
      focusAreas: z.array(titledBlockSchema),
      philosophyHeading: copy,
      philosophy: z.array(titledBlockSchema),
    }),
    certifications: z.object({
      ...pageBase('Certifications'),
      overviewHeading: copy,
      stats: z.object({
        total: statSchema,
        expert: statSchema,
        active: statSchema,
      }),
      essay: z.array(z.string()),
    }),
    awards: z.object({
      ...pageBase('Awards & Recognition'),
      journeyHeading: copy,
      stats: z.object({
        awards: statSchema,
        years: valueStatSchema,
        place: valueStatSchema,
      }),
      narrative: z.array(z.string()),
    }),
  }),
  cards: z.object({
    summary: copy,
    description: copy,
    keyAchievements: copy,
    keyProjects: copy,
    featured: copy,
    team: copy,
    duration: copy,
    achievements: copy,
    challenges: copy,
    learnings: copy,
    viewCode: copy,
    liveDemo: copy,
    documents: copy,
    diploma: copy,
    transcript: copy,
    relatedLinks: copy,
    level: copy,
    id: copy,
    expires: copy,
    issuedBy: copy,
    view: copy,
    download: copy,
  }),
  resume: z.object({
    cardTitle: copy,
    cardBody: copy,
    trigger: copy,
    dialogTitle: copy,
    dialogBody: copy,
    successMessage: copy,
    errorMessage: copy,
    form: z.object({
      personalLegend: copy,
      nameLabel: copy,
      namePlaceholder: copy,
      emailLabel: copy,
      emailPlaceholder: copy,
      companyLabel: copy,
      companyPlaceholder: copy,
      positionLabel: copy,
      positionPlaceholder: copy,
      messageLegend: copy,
      messageLabel: copy,
      messagePlaceholder: copy,
      cancel: copy,
      submit: copy,
    }),
  }),
  // All structural: these are the strings shown when something has already
  // gone wrong, so they must never be the reason something goes wrong.
  system: z.object({
    errorTitle: fixed('Oops, something went wrong'),
    errorBody: fixed(
      'Please try again later or contact me if the problem persists.',
    ),
    retry: fixed('Try Again'),
    notFoundTitle: fixed('404 - Page not found'),
    notFoundBody: fixed(
      'The page you are looking for does not exist or has been moved.',
    ),
    backHome: fixed('Back to home'),
    routeErrorTitle: fixed('Something went wrong'),
    routeErrorBody: fixed(
      'An unexpected error occurred while rendering this page. Reloading usually fixes it.',
    ),
    reload: fixed('Reload Page'),
    skipToContent: fixed('Skip to content'),
  }),
});

export type StrapiSiteContentResponse = z.infer<typeof strapiSiteContentSchema>;
